import { useState } from "react";
import { format, parse, isValid, startOfDay } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const TIMES = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const label12 = (t) => format(parse(t, "HH:mm", new Date()), "hh:mm a");

// value: "yyyy-MM-dd'T'HH:mm" (withTime) or "yyyy-MM-dd"; shown to users as dd/mm/yyyy
export function DateTimeField({ value, onChange, testId, className = "", withTime = true, allowPast = false, hideIcon = false }) {
  const [open, setOpen] = useState(false);
  const fmt = withTime ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd";
  const d = value ? parse(value, fmt, new Date()) : null;
  const valid = d && isValid(d);
  const time = valid && withTime ? format(d, "HH:mm") : "10:00";
  const emit = (day, t) => onChange(withTime ? `${format(day, "yyyy-MM-dd")}T${t}` : format(day, "yyyy-MM-dd"));
  const shown = valid ? format(d, withTime ? "dd/MM/yyyy, hh:mm a" : "dd/MM/yyyy") : "dd/mm/yyyy";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={`flex items-center justify-between gap-2 text-left ${className}`}>
          <span className="truncate">{shown}</span>
          {!hideIcon && <CalendarDays className="h-4 w-4 shrink-0 text-neutral-400" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-popover`}>
        <Calendar mode="single" selected={valid ? d : undefined} defaultMonth={valid ? d : undefined} weekStartsOn={1}
          disabled={allowPast ? undefined : { before: startOfDay(new Date()) }}
          onSelect={(day) => { if (day) { emit(day, time); if (!withTime) setOpen(false); } }} initialFocus />
        {withTime && (
          <div className="flex items-center gap-2 border-t p-3">
            <span className="text-xs font-medium text-neutral-500">Time</span>
            <select value={time} onChange={(e) => emit(valid ? d : new Date(), e.target.value)} data-testid={`${testId}-time`}
              className="flex-1 rounded-md border border-black/10 bg-white px-2 py-1.5 text-sm text-neutral-900 outline-none focus:ring-2 focus:ring-[#FF5A00]">
              {TIMES.map((t) => <option key={t} value={t}>{label12(t)}</option>)}
            </select>
            <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-done`} className="rounded-md bg-[#FF5A00] px-3 py-1.5 text-xs font-semibold text-white">Done</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
